<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { onMount } from 'svelte';
  import { replace } from 'svelte-spa-router';
  import TopBar from '../components/TopBar.svelte';
  import { session, applySession } from '../lib/session';
  import { api, ApiError } from '../lib/api';
  import { getInitData, isInsideTelegram } from '../lib/telegram';
  import { pluralUa } from '../lib/format';
  import type { CategoryDTO } from '../lib/types';

  let categories = $state<CategoryDTO[] | null>(null);
  let error = $state<string | null>(null);

  onMount(async () => {
    // Refresh session in the background (same pattern as HomePage)
    (async () => {
      try {
        if (isInsideTelegram()) applySession(await api.authMe(getInitData()));
        else applySession(await api.authSession());
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          replace(import.meta.env.DEV ? '/' : '/login');
        }
      }
    })();

    // Fetch catalog
    try {
      const { items } = await api.categories();
      categories = items;
    } catch (err) {
      error = err instanceof Error ? err.message : 'Не вдалось завантажити каталог';
    }
  });

  function materialsLabel(n: number): string {
    if (n === 0) return 'Скоро';
    return `${n} ${pluralUa(n, 'матеріал', 'матеріали', 'матеріалів')}`;
  }
</script>

<div class="screen">
  <TopBar title="База знань" />
  <div class="scroll">
    {#if error}
      <div class="error">{error}</div>
    {:else if categories && categories.length === 0}
      <div class="empty">Поки що тут порожньо</div>
    {:else if categories}
      <!-- Pinned: Favorites — always first, no stagger animation -->
      <a class="pinned" href="#/favorites">
        <div class="pinned-emoji"><Icon name="heart" size="lg" filled /></div>
        <div class="pinned-text">
          <div class="pinned-name">Улюблені</div>
          <div class="pinned-desc">Збережені матеріали</div>
        </div>
        <div class="pinned-chevron">›</div>
      </a>
      <div class="divider"></div>
      <div class="cat-list card-list">
        {#each categories as cat (cat.id)}
          {@const open = cat.hasAccess || cat.hasPartialAccess}
          <a class="cat-item" class:locked={!open} href={`#/section/${cat.id}`}>
            <div class="cat-emoji"><Icon name={cat.icon} size="lg" /></div>
            <div class="cat-text">
              <div class="cat-name">{cat.name}</div>
              <div class="cat-desc">
                {materialsLabel(cat.totalMaterials)}{#if cat.hasPartialAccess} · частковий доступ{/if}
              </div>
            </div>
            {#if open}
              <div class="cat-chevron">›</div>
            {:else}
              <div class="cat-lock" aria-label="Заблоковано" title="Потрібен доступ"><Icon name="lock" label="Заблоковано" /></div>
            {/if}
          </a>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .scroll {
    flex: 1;
    overflow-y: auto;
    padding: 12px 16px 100px;
  }
  .scroll::-webkit-scrollbar {
    width: 3px;
  }
  .scroll::-webkit-scrollbar-thumb {
    background: var(--border2);
    border-radius: 2px;
  }

  /* Pinned favorites card — branded gradient, stands out from regular cards. */
  .pinned {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 16px;
    background: linear-gradient(135deg, var(--dark) 0%, var(--dark3) 60%, var(--pu3) 100%);
    border-radius: 16px;
    color: var(--text-light);
    text-decoration: none;
    position: relative;
    overflow: hidden;
  }
  .pinned::after {
    content: '';
    position: absolute;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: rgba(251, 246, 236, 0.06);
    right: -30px;
    top: -30px;
    pointer-events: none;
  }
  .pinned-emoji {
    font-size: 26px;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(251, 246, 236, 0.15);
    border-radius: 12px;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
  }
  .pinned-text {
    flex: 1;
    min-width: 0;
    position: relative;
    z-index: 1;
  }
  .pinned-name {
    font-size: 16px;
    font-weight: 600;
  }
  .pinned-desc {
    font-size: 11px;
    opacity: 0.7;
    margin-top: 2px;
  }
  .pinned-chevron {
    color: rgba(251, 246, 236, 0.6);
    font-size: 22px;
    line-height: 1;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
  }

  .divider {
    height: 1px;
    background: var(--border);
    margin: 16px 4px;
  }

  .cat-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 14px;
    color: inherit;
    text-decoration: none;
    margin-bottom: 8px;
  }
  .cat-emoji {
    font-size: 24px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--pu-light);
    border-radius: 10px;
    flex-shrink: 0;
  }
  .cat-text {
    flex: 1;
    min-width: 0;
  }
  .cat-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cat-desc {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 2px;
  }
  .cat-chevron {
    color: var(--text-muted);
    font-size: 22px;
    line-height: 1;
    flex-shrink: 0;
  }

  /* Locked group — visible in the catalog but dimmed, with a lock instead of
     the chevron. Still tappable: opens the section page where the upsell lives. */
  .cat-item.locked {
    opacity: 0.55;
  }
  .cat-item.locked .cat-emoji {
    filter: grayscale(0.6);
  }
  .cat-lock {
    font-size: 15px;
    line-height: 1;
    flex-shrink: 0;
  }

  .error,
  .empty {
    text-align: center;
    padding: 40px 20px;
    color: var(--text-muted);
    font-size: 13px;
  }
</style>
